import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useHumhubSessionLogin } from "@/hooks/useHumhubSessionLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { SESSION_REFRESH_RETRY_DELAY_MS } from "@/lib/authSessionConfig";
import { getHumhubExpiresAt, getLatestOidcToken } from "@/lib/authSession";
import {
  buildAuthSessionSnapshot,
  calculateNextSessionRefreshDelay,
  getAdaptiveRefreshBufferMs,
  getNextSessionRefreshAt,
  getPreferredOidcToken,
  isExpiredOrWithinBuffer,
} from "@/lib/authSessionTiming";
import {
  OIDC_MAX_REFRESH_BUFFER_MS,
  OIDC_MIN_REFRESH_BUFFER_MS,
} from "@/lib/authSessionConfig";
import type { OidcRefreshFailureReason } from "@/lib/authSessionErrors";

const SESSION_REFRESH_RETRY_DELAYS_MS = [
  30_000, 60_000, 120_000, 300_000,
] as const;
const SESSION_REFRESH_MAX_RETRY_DELAY_MS = 300_000;
const SESSION_REFRESH_RETRY_JITTER_RATIO = 0.2;

const isTransientRefreshFailure = (
  status: OidcRefreshFailureReason,
): status is "network_error" | "rate_limited" | "http_error" =>
  status === "network_error" ||
  status === "rate_limited" ||
  status === "http_error";

export const useSessionRefreshManager = ({ enabled }: { enabled: boolean }) => {
  const auth = useAuth();
  const { loginToHumhubSession } = useHumhubSessionLogin();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const timerRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const isSyncingRef = useRef(false);
  const retryAttemptRef = useRef(0);
  const retryDeadlineRef = useRef<number | null>(null);
  const retryPendingRef = useRef(false);
  const syncSessionRef = useRef<() => Promise<void>>(async () => undefined);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleAfter = useCallback(
    (delayMs: number, task: () => void) => {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        task();
      }, delayMs);
    },
    [clearTimer],
  );

  const getActiveOidcToken = useCallback(
    () =>
      getPreferredOidcToken(
        buildAuthSessionSnapshot(auth),
        getLatestOidcToken(),
      ),
    [auth],
  );

  const resetRetryState = useCallback(() => {
    retryAttemptRef.current = 0;
    retryDeadlineRef.current = null;
    retryPendingRef.current = false;
  }, []);

  const scheduleRetry = useCallback(() => {
    const delayIndex = Math.min(
      retryAttemptRef.current,
      SESSION_REFRESH_RETRY_DELAYS_MS.length - 1,
    );
    const baseDelay = SESSION_REFRESH_RETRY_DELAYS_MS[delayIndex];
    const jitter = Math.floor(
      baseDelay * SESSION_REFRESH_RETRY_JITTER_RATIO * Math.random(),
    );
    const retryDelay = Math.min(
      baseDelay + jitter,
      SESSION_REFRESH_MAX_RETRY_DELAY_MS,
    );

    retryAttemptRef.current += 1;
    retryDeadlineRef.current = Date.now() + retryDelay;
    retryPendingRef.current = true;

    if (!isMountedRef.current || window.navigator.onLine === false) {
      clearTimer();
      return;
    }

    scheduleAfter(retryDelay, () => {
      retryDeadlineRef.current = null;
      void syncSessionRef.current();
    });
  }, [clearTimer, scheduleAfter]);

  const syncSession = useCallback(async () => {
    if (!enabled || auth.isLoading || auth.activeNavigator) {
      return;
    }

    if (window.navigator.onLine === false) {
      retryPendingRef.current = true;
      clearTimer();
      return;
    }

    if (isSyncingRef.current) {
      return;
    }

    isSyncingRef.current = true;

    try {
      let tokenInfo = getActiveOidcToken();

      if (
        !tokenInfo ||
        isExpiredOrWithinBuffer(
          tokenInfo.expiresAt,
          getAdaptiveRefreshBufferMs(tokenInfo.expiresAt, {
            maxBufferMs: OIDC_MAX_REFRESH_BUFFER_MS,
            minBufferMs: OIDC_MIN_REFRESH_BUFFER_MS,
          }) ?? OIDC_MIN_REFRESH_BUFFER_MS,
        )
      ) {
        const refreshResult = await refreshAccessToken();

        if (refreshResult.status !== "success") {
          if (isTransientRefreshFailure(refreshResult.status)) {
            scheduleRetry();
          } else {
            resetRetryState();
            clearTimer();
            console.error(
              `Session token refresh requires explicit recovery: ${refreshResult.status}`,
            );
          }
          return;
        }

        tokenInfo = refreshResult.tokenInfo;
        resetRetryState();
      }

      await loginToHumhubSession(tokenInfo.token, tokenInfo.expiresAt);
      resetRetryState();

      if (isMountedRef.current) {
        const nextDelay =
          calculateNextSessionRefreshDelay({
            oidcToken: getActiveOidcToken(),
            humhubExpiresAt: getHumhubExpiresAt(),
          }) ?? SESSION_REFRESH_RETRY_DELAY_MS;

        scheduleAfter(nextDelay, () => {
          void syncSession();
        });
      }
    } catch (error) {
      console.error("Session background refresh failed:", error);
      if (isMountedRef.current) {
        scheduleRetry();
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [
    auth.activeNavigator,
    auth.isLoading,
    clearTimer,
    enabled,
    getActiveOidcToken,
    loginToHumhubSession,
    refreshAccessToken,
    resetRetryState,
    scheduleRetry,
    scheduleAfter,
  ]);

  syncSessionRef.current = syncSession;

  const scheduleNextRefresh = useCallback(() => {
    if (!enabled || auth.isLoading || auth.activeNavigator) {
      clearTimer();
      return;
    }

    const currentHumhubExpiresAt = getHumhubExpiresAt();
    const nextRefreshAt = getNextSessionRefreshAt({
      oidcToken: getActiveOidcToken(),
      humhubExpiresAt: currentHumhubExpiresAt,
    });

    if (!nextRefreshAt) {
      clearTimer();
      return;
    }

    if (Date.now() >= nextRefreshAt) {
      clearTimer();
      void syncSession();
      return;
    }

    const delayMs =
      calculateNextSessionRefreshDelay({
        oidcToken: getActiveOidcToken(),
        humhubExpiresAt: currentHumhubExpiresAt,
      }) ?? SESSION_REFRESH_RETRY_DELAY_MS;

    scheduleAfter(delayMs, () => {
      void syncSession();
    });
  }, [
    auth.activeNavigator,
    auth.isLoading,
    clearTimer,
    enabled,
    getActiveOidcToken,
    scheduleAfter,
    syncSession,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    scheduleNextRefresh();

    const resumeRefresh = () => {
      if (!retryPendingRef.current) {
        scheduleNextRefresh();
        return;
      }

      if (
        window.navigator.onLine === false ||
        document.visibilityState === "hidden"
      ) {
        return;
      }

      if (timerRef.current !== null) {
        return;
      }

      if (retryDeadlineRef.current !== null) {
        const remainingDelay = Math.max(
          retryDeadlineRef.current - Date.now(),
          0,
        );
        scheduleAfter(remainingDelay, () => {
          retryDeadlineRef.current = null;
          void syncSessionRef.current();
        });
        return;
      }

      void syncSessionRef.current();
    };

    const pauseRefresh = () => {
      if (retryPendingRef.current) {
        clearTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resumeRefresh();
      }
    };

    window.addEventListener("online", resumeRefresh);
    window.addEventListener("offline", pauseRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimer();
      window.removeEventListener("online", resumeRefresh);
      window.removeEventListener("offline", pauseRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearTimer, enabled, scheduleAfter, scheduleNextRefresh]);
};
